import { SearchTeam, SearchUserEnriched } from 'lib-search-types';
import { User } from 'lib-shared-types-api';
import { useEffect, useMemo, useState } from 'react';

import { AwayStatus } from 'apis/presence';
import { FavoritesPresenceListSection } from 'components/presence/components/favoritesPresenceListSection';
import { TeamPresenceListSections } from 'components/presence/components/teamPresenceListSections';
import { sortMembers } from 'components/presence/utils';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useFavoriteAgents } from 'utils/useFavoriteAgents';

export type TeamWithMembers = {
  id: string;
  name: string;
  members: SearchUserEnriched[];
};

type PresenceListSectionsProps = {
  profile: User;
  awayStatuses: AwayStatus[];
  members: SearchUserEnriched[];
  teams: readonly SearchTeam[];
  isLoading: boolean;
  isError: boolean;
};

export const PresenceListSections = ({
  profile,
  awayStatuses,
  members,
  teams,
  isLoading,
  isError,
}: PresenceListSectionsProps): JSX.Element => {
  const { presenceSidebarShowSelf } = useFeatureFlag([
    'presence-sidebar-show-self',
  ]);

  const { favoriteAgentIds, addFavorite, removeFavorite } = useFavoriteAgents();

  // Frozen favorites state - prevents list jumping while dropdown is open
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [frozenFavoriteIds, setFrozenFavoriteIds] =
    useState<readonly string[]>(favoriteAgentIds);

  useEffect(() => {
    if (!isDropdownOpen) {
      setFrozenFavoriteIds(favoriteAgentIds);
    }
  }, [favoriteAgentIds, isDropdownOpen]);

  const favoriteMembers = useMemo(() => {
    const favorites = members.filter(
      (member) =>
        member.id === profile.id || frozenFavoriteIds.includes(member.id),
    );
    const sorted = sortMembers(favorites);
    const ownMember = sorted.find((member) => member.id === profile.id);
    const others = sorted.filter((member) => member.id !== profile.id);
    return ownMember ? [ownMember, ...others] : others;
  }, [members, profile.id, frozenFavoriteIds]);

  const membersById = useMemo(
    () => new Map(members.map((member) => [member.id, member] as const)),
    [members],
  );

  const teamsWithMembers: TeamWithMembers[] = useMemo(
    () =>
      teams
        .map((team) => ({
          id: team.id,
          name: team.name,
          members: sortMembers(
            (team.agents ?? [])
              .map((agentId) => membersById.get(agentId))
              .filter(
                (member): member is SearchUserEnriched =>
                  !!member &&
                  (presenceSidebarShowSelf || member.id !== profile.id),
              ),
          ),
        }))
        .filter((team) => team.members.length > 0),
    [teams, membersById, profile.id, presenceSidebarShowSelf],
  );

  return (
    <>
      <FavoritesPresenceListSection
        profile={profile}
        awayStatuses={awayStatuses}
        members={favoriteMembers}
        loading={isLoading}
        favoriteAgentIds={favoriteAgentIds}
        onAddToFavorites={addFavorite}
        onRemoveFromFavorites={removeFavorite}
        onDropdownOpenChange={setIsDropdownOpen}
      />
      <TeamPresenceListSections
        profile={profile}
        awayStatuses={awayStatuses}
        teams={teamsWithMembers}
        loading={isLoading}
        error={isError}
        favoriteAgentIds={favoriteAgentIds}
        onAddToFavorites={addFavorite}
        onRemoveFromFavorites={removeFavorite}
      />
    </>
  );
};
